import * as Sentry from '@sentry/nextjs'
import Axios, { AxiosRequestConfig } from 'axios'
import { camelizeKeys } from 'humps'
import router from 'next/router'
import { AuthService } from 'src/services/AuthService'
import { HOSTNAME } from 'src/utils/env'
import { BASE_PATH, NO_AUTH_PATHS, SIGN_IN } from 'src/utils/routes'
import { ApiResponse } from './types'
import { NotAuthorizedResponse } from './types/auth'

const axiosClient = Axios.create({
  baseURL: HOSTNAME ? `https://${HOSTNAME}` : undefined,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

const errorResponse = (...messages: string[]): ApiResponse => ({
  data: undefined,
  error: {
    errorMessages: [...messages],
  },
})
let hasReportedEmptyRouterPath = false

axiosClient.interceptors.response.use(
  (response) => {
    const data = camelizeKeys(response.data) as
      | ApiResponse['data']
      | NotAuthorizedResponse
    try {
      if (data && 'redirectTo' in data) {
        window.location.assign(data.redirectTo)
        return { ...response, data: undefined }
      }
    } catch {}
    return { ...response, data }
  },
  (error) => {
    if (error.code === 'ECONNABORTED')
      return handleErrorWithMessage(
        error,
        'タイムアウトしました。電波の良いところで再度お試しください。',
      )
    if (shouldRedirect(currentPath(), error.response.status))
      AuthService.signOut().finally(() => window.location.assign(SIGN_IN))

    if (error.response.status === 413)
      return handleErrorWithMessage(
        error,
        '添付ファイルのサイズが最大値を超えています。お手数ですがファイルサイズを圧縮して再度お試しください。',
      )
    if (error.response.status === 401)
      return handleErrorWithMessage(
        error,
        '権限がありません。再度ログインし直してください',
      )
    const response = camelizeKeys(error.response?.data) as any
    if (
      `${error.response.status}`.startsWith('5') ||
      !response?.errorMessages
    ) {
      Sentry.captureException(new Error(response?.errorMessages))
      return handleErrorWithMessage(
        error,
        'エラーが発生しました。時間をあけて再度お試しいただくか、運営者にお問い合わせください',
      )
    }
    return Promise.resolve({
      ...error,
      error: response,
    })
  },
)

const currentPath = () => {
  if (typeof window !== 'undefined') return window.location.pathname
  if (typeof router.pathname === 'string' && router.pathname)
    return router.pathname
  if (!hasReportedEmptyRouterPath) {
    Sentry.captureMessage('router.pathname is empty in currentPath()')
    hasReportedEmptyRouterPath = true
  }
  return ''
}

const normalizePath = (path: string) => {
  const pathname = path.split(/[?#]/)[0] || ''
  if (pathname === BASE_PATH || pathname.startsWith(`${BASE_PATH}/`)) {
    const trimmed = pathname.slice(BASE_PATH.length)
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  }
  return pathname
}

export const shouldIgnorePath = (path: string) => {
  const normalizedPath = normalizePath(path)
  return NO_AUTH_PATHS.some((targetPath) =>
    normalizedPath.startsWith(targetPath),
  )
}

const shouldRedirect = (currentPath: string, errorStatus: number) => {
  const shouldIgnore = shouldIgnorePath(currentPath)
  if (shouldIgnore) return false
  if (errorStatus === 401 || errorStatus === 403) return true
  return false
}

const handleErrorWithMessage = (error: any, message: string) => {
  return Promise.resolve({
    ...error,
    ...errorResponse(message),
  })
}

type ApiClientInstance = {
  get: <T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<T>>
  post: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<T>>
  put: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<T>>
  delete: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<T>>
  patch: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<T>>
}
export const ApiClient = axiosClient as ApiClientInstance

export const fetcher = async (url: string): Promise<any> => {
  const { data } = await ApiClient.get(url)
  return data
}
